import React, { useEffect, useState } from 'react';
import PaymentRequisitionsExportButton from '../components/PaymentRequisitionsExportButton';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Attachment,
  PaymentRequisition,
  useGetPaymentRequisitionsQuery,
  useLazyGetProcurementProcessesQuery,
  usePatchPaymentRequisitionsByIdMutation
} from '../../../redux/vmsApi';

import config from "../../../config";

import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import NumbersIcon from '@mui/icons-material/Numbers';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import DropzoneArea from '../../common/DropzoneArea';
import { getCurrentDate, getDefaultDueDate } from '../../../utils/dateUtil';
import { useRouter } from "../../../hooks/useRouter";

const PaymentsHistory: React.FC = () => {
  const router = useRouter();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [filters, setFilters] = useState({
    amount: '',
    status: 'Paid',
    supplierName: '',
    paymentMethod: '',
    paymentType: '',
    development: '',
    project: '',
    department: '',
    reqNumber: '',
    purchaseOrderNumber: '',
    fromDate: '2025-01-06',
    toDate: getCurrentDate(),
    isOutstandingPayment: false,
    currency: '',
    referenceNumber: '',
    batchNumber: '',
    reportByEmail: false,
    reportName: ''
  });
  const [triggerProcurementProcess] = useLazyGetProcurementProcessesQuery();
  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [currentOpenRow, setCurrentOpenRow] = useState<string | null>(null);
  const [reportByEmailEnabled, setReportByEmailEnabled] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [apiParams, setApiParams] = useState({ ...filters, skip: 0, limit: 10 });
  const [referenceNumbersByProcessId, setReferenceNumbersByProcessId] = useState<Record<string, string>>({});

  const { data: paymentRequisitions, isLoading, isError } =
    useGetPaymentRequisitionsQuery(apiParams);

  useEffect(() => {
    const missingProcessIds = Array.from(new Set(
      (paymentRequisitions || [])
        .filter((req: any) => !req?.purchaseOrder?.purchaseOrderNumber && req?.procurementProcessId)
        .map((req: any) => req.procurementProcessId)
        .filter((processId: string | undefined): processId is string => Boolean(processId))
        .filter((processId: string) => !referenceNumbersByProcessId[processId])
    ));

    if (missingProcessIds.length === 0) return;

    let cancelled = false;

    Promise.all(
      missingProcessIds.map(async (procurementProcessId) => {
        const { data } = await triggerProcurementProcess({ procurementProcessId });
        const process = data?.[0];
        return {
          procurementProcessId,
          referenceNumber: process?.requisition?.reqNumber || procurementProcessId,
        };
      })
    ).then((results) => {
      if (cancelled) return;

      setReferenceNumbersByProcessId((prev) => {
        const next = { ...prev };
        results.forEach(({ procurementProcessId, referenceNumber }) => {
          next[procurementProcessId] = referenceNumber;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [paymentRequisitions, triggerProcurementProcess, referenceNumbersByProcessId]);

  const handleFilterChange = (field: string, value: string | boolean) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setApiParams({
      ...filters,
      skip: 0,
      limit: pageSize,
    });
    setPage(0);
    setReportByEmailEnabled(false);
    handleFilterChange('reportName', '');
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setApiParams((prevParams) => ({
      ...prevParams,
      skip: newPage * pageSize,
    }));
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(0);
    setApiParams((prevParams: any) => ({
      ...prevParams,
      limit: newSize,
      skip: 0,
    }));
  };

  //form patching
  const [updatePaymentReq] = usePatchPaymentRequisitionsByIdMutation();
  const handleFormChange = (field: keyof PaymentRequisition, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<PaymentRequisition>>({
    paymentMethod: '',
    paymentType: undefined,
    referenceNumber: '',
    amount: 0,
    rate: 1,
    dueDate: getDefaultDueDate(),
    status: 'Scheduled',
    priority: 0,
    documents: []
  });
  const [currentPaymentReq, setCurrentPaymentReq] = useState<PaymentRequisition | null>(null);
  const selectedPurchaseOrder = currentPaymentReq?.purchaseOrder;
  const paymentCurrency = selectedPurchaseOrder?.quotation?.currency || currentPaymentReq?.currency || 'USD';
  const paymentReferenceValue = selectedPurchaseOrder?.quotation?.grandTotal ?? currentPaymentReq?.amount;
  const handleEditClick = (paymentReq: PaymentRequisition) => {
    setCurrentPaymentReq(paymentReq);
    setFormData({
      ...paymentReq
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (formData?.dueDate && formData?.referenceNumber && formData?.paymentMethod && formData.amount)
      try {


        const commonData = {
          status: formData?.status,
          lastActionedDate: new Date().toISOString(),
          referenceNumber: formData.referenceNumber,
          documents: attachments
        };

        if (currentPaymentReq && currentPaymentReq.id) {

          await updatePaymentReq({
            id: currentPaymentReq.id,
            paymentRequisitionPartial: {
              ...commonData,
            }
          }).unwrap();
        }

        handleEditDialogClose();
      } catch (error) {
        console.error("Error saving payment requisition:", error);
      }
  };
  const handleEditDialogClose = () => {
    setIsDialogOpen(false);
    setCurrentPaymentReq(null);
    setFormData({
      paymentMethod: '',
      referenceNumber: '',
      amount: 0,
      rate: 1,
      dueDate: '',
      status: 'Awaiting Approval'
    });
  };

  //end form  patching

  const handleDialogOpen = async (procurementProcessId: string, documentType: string) => {
    setOpenDialog(null);
    if (documentType === 'paymentRequisition') {
      setOpenDialog(procurementProcessId);
      return;
    }

    if (documentType === 'POP') {
      setOpenDialog(procurementProcessId);
      return;
    }
    const { data: updatedProcurementProcesses } = await triggerProcurementProcess({ procurementProcessId })

    if (updatedProcurementProcesses?.[0]) {
      const { data: updatedProcurementProcesses } = await triggerProcurementProcess({ procurementProcessId })
      if (updatedProcurementProcesses?.[0]) {
        console.log("updatedProcurementProcesses?.[0]", updatedProcurementProcesses?.[0]);
        const documentUrl = `${config.CDN_URL}/procurement/${documentType === 'purchaseOrder'
          ? updatedProcurementProcesses[0].purchaseOrderDocument?.name
          : updatedProcurementProcesses[0].requisitionDocument?.name
          }`;

        if (documentUrl) {
          setOpenDialog(documentUrl);
        } else {
          console.error('Document URL not found');
          setOpenDialog(null);
        }

        console.log("<<<<----documentUrl----->", documentUrl);
      }


    } else {
      console.error('Procurement process not found');
      setOpenDialog(null);
    }
  };

  const handleDialogClose = () => setOpenDialog(null);

  const closeAction = () => setCurrentOpenRow(null);
  const handleToggleDropdown = (rowId: string) => {
    setCurrentOpenRow(prev => (prev === rowId ? null : rowId));
  };

  const handleViewProcessDetails = (procurementProcessId: string) => {
    window.open(`/procurement/procurementProcess/${procurementProcessId}`, '_blank');
    setCurrentOpenRow(null);
  };

  if (isLoading) return <CircularProgress />;
  if (isError) return <div>Error fetching payment requisitions.</div>;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ padding: 3, borderRadius: 2, mb: 3, bgcolor: '#f9f9f9', boxShadow: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 'bold' }}>
          Payment Requisitions
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            label="Supplier Name"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
            value={filters.supplierName}
            onChange={e => handleFilterChange('supplierName', e.target.value)}
          />

          <TextField
            label="Requisition Number"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.reqNumber}
            onChange={(e) => handleFilterChange('reqNumber', e.target.value)}
          />
          <TextField
            label="Purchase Order Number"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.purchaseOrderNumber}
            onChange={(e) => handleFilterChange('purchaseOrderNumber', e.target.value)}
          />

          <TextField
            label="Development"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
            value={filters.development}
            onChange={e => handleFilterChange('development', e.target.value)}
          />

          <TextField
            label="Project"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
            value={filters.project}
            onChange={e => handleFilterChange('project', e.target.value)}
          />

          <TextField
            label="Department"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
            value={filters.department}
            onChange={e => handleFilterChange('department', e.target.value)}
          />

          <TextField
            label="Reference Number"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
            value={filters.referenceNumber}
            onChange={e => handleFilterChange('referenceNumber', e.target.value)}
          />

          <TextField
            label="Batch Number"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
            value={filters.batchNumber}
            onChange={e => handleFilterChange('batchNumber', e.target.value)}
          />

          <TextField
            label="From Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
            value={filters.fromDate}
            onChange={e => handleFilterChange('fromDate', e.target.value)}
          />

          <TextField
            label="To Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
            value={filters.toDate}
            onChange={e => handleFilterChange('toDate', e.target.value)}
          />

          <FormControl
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
          >
            <InputLabel id="currency-select-label">Currency</InputLabel>
            <Select
              labelId="currency-select-label"
              value={filters.currency || ''}
              label="Currency"
              onChange={(e) => handleFilterChange('currency', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="ZAR">ZAR</MenuItem>
              <MenuItem value="ZIG">ZIG</MenuItem>
            </Select>
          </FormControl>
          <FormControl
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
          >
            <InputLabel id="payment-method-select-label">Payment Method</InputLabel>
            <Select
              labelId="payment-method-select-label"
              value={filters.paymentMethod || ''}
              label="Payment Method"
              onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Nostro">Nostro</MenuItem>

            </Select>
          </FormControl>

          <FormControl
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
          >
            <InputLabel id="payment-type-select-label">Payment Type</InputLabel>
            <Select
              labelId="payment-type-select-label"
              value={filters.paymentType || ''}
              label="Payment Type"
              onChange={(e) => handleFilterChange('paymentType', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="DEPOSIT">DEPOSIT</MenuItem>
              <MenuItem value="INTERIM">INTERIM</MenuItem>
              <MenuItem value="FINAL PAYMENT">FINAL PAYMENT</MenuItem>
              <MenuItem value="ONCE OFF">ONCE OFF</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Requisition Amount"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
            value={filters.amount}
            onChange={e => handleFilterChange('amount', e.target.value)}
          />


          <FormControlLabel
            control={
              <Switch
                checked={filters.isOutstandingPayment}
                onChange={e => handleFilterChange('isOutstandingPayment', e.target.checked)}
                color="primary"
              />
            }
            label="Show Outstanding Only"
            sx={{ ml: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={reportByEmailEnabled}
                onChange={e => {
                  const isEnabled = e.target.checked;
                  setReportByEmailEnabled(isEnabled);
                  handleFilterChange('reportByEmail', isEnabled);
                  if (!isEnabled) handleFilterChange('reportName', '');
                }}
                color="primary"
              />
            }
            label="Email Report"
          />

          {reportByEmailEnabled && (
            <TextField
              label="Report Name"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
              value={filters.reportName}
              onChange={e => handleFilterChange('reportName', e.target.value)}
            />
          )}
        </Box>

        <Stack direction="row" spacing={2} mt={2}>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={handleApplyFilters}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': { bgcolor: '#e3f2fd', borderColor: '#1565c0' },
            }}
          >
            Apply Filters
          </Button>
          <PaymentRequisitionsExportButton
            apiParams={apiParams}
            reportLabel="Payments History"
            fileNamePrefix="payments-history"
          />
        </Stack>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reference</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Title</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Development</TableCell>



              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Supplier</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Method/Reference Number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status/Batch</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paymentRequisitions && paymentRequisitions.length > 0 ? (
              paymentRequisitions.map((req: any) => {

                return (
                  <TableRow key={req.id} hover>
                    <TableCell>
                      {req.purchaseOrder?.purchaseOrderNumber || referenceNumbersByProcessId[req.procurementProcessId] || req.procurementProcessId || req.purchaseOrderNumber || 'N/A'}
                    </TableCell>

                    <TableCell>{req.purchaseOrder?.title || req.title || 'N/A'}</TableCell>
                    <TableCell>{req?.development || 'N/A'},{req?.project || 'N/A'},{req?.department || 'N/A'}</TableCell>
                    <TableCell>{req.purchaseOrder?.quotation?.supplier?.name || 'N/A'}</TableCell>

                    <TableCell>
                      <Box
                      >

                        <Box
                          sx={{
                            backgroundColor: '#e0f7fa',
                            color: '#006064',
                            paddingX: 1,
                            paddingY: 0.3,
                            borderRadius: '8px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                          }}
                        >
                          {req.paymentMethod},<br></br>{req.paymentType}/{req.referenceNumber}
                        </Box>


                      </Box>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {req.currency} {req.amount?.toFixed(2)}
                    </TableCell>


                    <TableCell>
                      <Box
                        sx={{
                          bgcolor:
                            req.status === 'Paid' ? '#4caf50' :
                              req.status === 'Rejected' ? '#f44336' :
                                req.status === 'Approved' ? '#2196f3' :
                                  req.status === 'Prioritized' ? '#ffb300' :
                                    req.status === 'Scheduled' ? '#9c27b0' :
                                      '#ff9800',
                          color: 'white',
                          borderRadius: '4px',
                          px: 1,
                          py: 0.5,
                          display: 'inline-block',
                          fontWeight: 500,
                          textAlign: 'center'
                        }}

                      >
                        {req.status}
                      </Box>
                      {req.batchNumber}
                    </TableCell>


                    <TableCell align="right">
                      <Box sx={{ position: "relative", display: "inline-block" }}>
                        <Button
                          sx={{
                            bgcolor: "primary.main",
                            color: "white",
                            "&:hover": { bgcolor: "primary.dark" },
                          }}
                          onClick={() => handleToggleDropdown(req.id || "")}
                        >
                          Actions
                        </Button>

                        {currentOpenRow === req.id && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: "100%",
                              right: 0,
                              zIndex: 1,
                              mt: 1,
                              minWidth: "150px",
                              bgcolor: "white",
                              boxShadow: 3,
                              borderRadius: 1,
                              overflow: "hidden",
                            }}
                          >
                            <IconButton
                              onClick={closeAction}
                              sx={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                bgcolor: "white",
                                border: "1px solid red",
                                color: "red",
                                "&:hover": { bgcolor: "grey.100" },
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                            <Button
                              sx={{
                                display: "block",
                                mt: 10,
                                width: "100%",
                                textAlign: "left",
                                px: 2,
                                py: 1,
                                "&:hover": { bgcolor: "grey.100" },
                              }}
                              onClick={() => handleViewProcessDetails(req.purchaseOrder?.procurementProcessId || req.procurementProcessId || "")}
                            >
                              Procurement Process Details
                            </Button>

                            <Button
                              sx={{
                                display: "block",
                                width: "100%",
                                textAlign: "left",
                                px: 2,
                                py: 1,
                                "&:hover": { bgcolor: "grey.100" },
                              }}
                              onClick={() => handleDialogOpen(req.purchaseOrder?.procurementProcessId || req.procurementProcessId || '', req.purchaseOrder ? 'purchaseOrder' : 'requisition')}
                            >
                              View Purchase Order
                            </Button>

                            <Button
                              sx={{
                                display: "block",
                                width: "100%",
                                textAlign: "left",
                                px: 2,
                                py: 1,
                                "&:hover": { bgcolor: "grey.100" },
                              }}
                              onClick={() => handleDialogOpen(req.documents[0].url || '', 'POP')}
                            >
                              POP
                            </Button>

                            <Button
                              sx={{
                                display: "block",
                                width: "100%",
                                textAlign: "left",
                                px: 2,
                                py: 1,
                                "&:hover": { bgcolor: "grey.100" },
                              }}
                              onClick={() => handleDialogOpen(req.paymentRequisitionDocument.url || '', 'paymentRequisition')}
                            >
                              Payment Requisition
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No payment requisitions found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Button
          onClick={() => handlePageChange(Math.max(page - 1, 0))}
          disabled={page === 0}
          variant="contained"
          sx={{ mr: 2 }}
        >
          Previous
        </Button>

        <Typography variant="body1">Page {page + 1}</Typography>

        <Button
          onClick={() => handlePageChange(page + 1)}
          disabled={paymentRequisitions?.length === undefined || paymentRequisitions.length < pageSize}
          variant="contained"
          sx={{ ml: 2 }}
        >
          Next
        </Button>
      </Box>

      <Box mt={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body2" sx={{ mr: 2 }}>Rows per page:</Typography>
        <FormControl variant="outlined" size="small">
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            sx={{ bgcolor: '#f0f4f7' }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Dialog open={!!openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
        <Box p={2}>
          {openDialog && (
            <iframe
              src={openDialog}
              title="Purchase Order Document"
              width="100%"
              height="500px"
              style={{ border: 'none' }}
            />
          )}
          <Box textAlign="right" mt={2}>
            <Button onClick={handleDialogClose} variant="contained" color="primary">
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentPaymentReq ? 'Confirm Payment' : 'Create Payment Requisition'}
        </DialogTitle>
        <DialogContent>

          <Card
            elevation={3}
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%)',
              borderLeft: '4px solid #1976d2',
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 24px 0 rgba(0,0,0,0.15)'
              }
            }}
          >
            <Box display="flex" alignItems="center" mb={1}>
              <ReceiptLongIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1976d2',
                  letterSpacing: '0.5px'
                }}
              >
                Payment Requisition Details
              </Typography>
            </Box>

            <Divider sx={{ mb: 2, borderColor: 'rgba(25, 118, 210, 0.2)' }} />

            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={1.5}>
                  <NumbersIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    <Box component="span" fontWeight={600}>PO
                      Reference:</Box> {selectedPurchaseOrder?.purchaseOrderNumber || referenceNumbersByProcessId[currentPaymentReq?.procurementProcessId || ''] || currentPaymentReq?.procurementProcessId || 'N/A'}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" mb={1.5}>
                  <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    <Box component="span" fontWeight={600}>Allocation:</Box>
                    <Box component="span" display="block" ml={2}>
                      {selectedPurchaseOrder?.development || currentPaymentReq?.development || 'N/A'},
                    </Box>
                    <Box component="span" display="block" ml={2}>
                      {currentPaymentReq?.project || 'N/A'}
                    </Box>
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={1.5}>
                  <DescriptionIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    <Box component="span" fontWeight={600}>Details:</Box> {selectedPurchaseOrder?.title || currentPaymentReq?.title || 'N/A'}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" mb={1.5}>
                  <CurrencyExchangeIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    <Box component="span" fontWeight={600}>Currency:</Box>
                    <Box
                      component="span"
                      sx={{
                        bgcolor: '#e3f2fd',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        ml: 1
                      }}
                    >
                      {paymentCurrency}
                    </Box>
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center">
                  <AttachMoneyIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    <Box component="span" fontWeight={600}>PO Value:</Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        color: '#2e7d32',
                        ml: 1
                      }}
                    >
                      {typeof paymentReferenceValue === 'number' ? paymentReferenceValue.toFixed(2) : 'N/A'}
                    </Box>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>

          <TextField
            label="Reference Number"
            fullWidth
            margin="normal"
            value={formData.referenceNumber || ''}
            onChange={(e) => handleFormChange('referenceNumber', e.target.value)}
            required
          />


          <Box display="flex" gap={2} mt={2}>
            <TextField
              select
              label="Payment Method"
              fullWidth
              value={formData.paymentMethod || ''}
              onChange={(e) => handleFormChange('paymentMethod', e.target.value)}
              required
            >
              <MenuItem value="Nostro">Nostro</MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
            </TextField>

            <TextField
              label="Amount"
              fullWidth
              type="number"
              value={formData.amount || 0}
              onChange={(e) => handleFormChange('amount', parseFloat(e.target.value))}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />

            <TextField
              label="Rate"
              fullWidth
              type="number"
              value={formData.rate || 0}
              onChange={(e) => handleFormChange('rate', parseFloat(e.target.value))}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />


            <TextField
              label="USD Equivalent"
              fullWidth
              value={
                formData.amount && formData.rate && formData.rate > 0
                  ? (formData.amount / formData.rate).toFixed(2)
                  : ''
              }
              InputProps={{
                readOnly: true,
              }}
            />


          </Box>

          <Box mt={2}>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Due Date"
                value={formData.dueDate ? new Date(formData.dueDate) : null}
                onChange={(date) => handleFormChange('dueDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true
                  }
                }}
                minDate={new Date()}
              />
            </LocalizationProvider>
          </Box>
          <Box display="flex" gap={2} mt={2}>

            <FormControlLabel

              control={
                <Switch
                  checked={formData.status === "Paid"}
                  onChange={(e) =>
                    handleFormChange('status', e.target.checked ? "Paid" : "Scheduled")
                  }
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center">
                  <Typography variant="body1">Paid ?</Typography>
                  <Box
                    sx={{
                      ml: 1,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: formData.status === "Paid" ? '#4caf50' : '#f44336',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: 12,
                      fontWeight: 'bold'
                    }}
                  >
                    {formData.status === "Paid" ? 'YES' : 'NO'}
                  </Box>
                </Box>
              }
              sx={{
                ml: 0,
                mt: 2,
                padding: '8px 16px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: '#f9f9f9'
              }}
            />
          </Box>
          <br></br>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'textSecondary' }}>
              Attachments
            </Typography>
            <DropzoneArea
              setAttachments={setAttachments}
              attachments={attachments}
              fieldName='documents'
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!formData.referenceNumber || !formData.paymentMethod || !formData.amount || !formData.dueDate}
          >
            {currentPaymentReq ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default PaymentsHistory;